import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { GSIcon } from 'src/components/shared/GSIcon'
import { TableTabs } from 'src/components/shared/table/table-tabs/TableTabs'
import type { TableTabItem } from 'src/components/shared/table/table-tabs/TableTabs'
import { InternalAccessTab } from 'src/constants/internal-access/internalAccessTabs'
import {
  getInternalRoles,
  getInternalRolesLoading,
  getInternalUsers,
  getInternalUsersLoading,
} from 'src/selectors/internalAccess'
import { addS } from 'src/utils/baseUtils'

interface Props {
  selectedTab: InternalAccessTab
  availableTabs: InternalAccessTab[]
  onChange(tab: InternalAccessTab): void
}

// Central icons paint via `currentColor`, so colour is set through the text colour.
const selectedIconStyles = tw`text-primary-600`
const iconStyles = tw`text-shades-1000`

const InternalAccessTabsComponent = ({ selectedTab, availableTabs, onChange }: Props) => {
  const users = useSelector(getInternalUsers)
  const roles = useSelector(getInternalRoles)
  const isUsersLoading = useSelector(getInternalUsersLoading)
  const isRolesLoading = useSelector(getInternalRolesLoading)

  const tabs = useMemo(() => {
    // No count during the first load — "0 users" would read as an empty list.
    const usersSubtitle =
      isUsersLoading && users.length === 0 ? '' : `${users.length} ${addS(users.length, 'user')}`
    const rolesSubtitle =
      isRolesLoading && roles.length === 0 ? '' : `${roles.length} ${addS(roles.length, 'role')}`

    const items: Array<TableTabItem<InternalAccessTab>> = [
      {
        value: InternalAccessTab.Users,
        title: 'Users',
        subtitle: usersSubtitle,
        dataTestId: 'internal-access-tab-users',
        renderIcon: (selected: boolean) => {
          return (
            <GSIcon iconName="gs_user" size={20} css={selected ? selectedIconStyles : iconStyles} />
          )
        },
      },
      {
        value: InternalAccessTab.Roles,
        title: 'Roles',
        subtitle: rolesSubtitle,
        dataTestId: 'internal-access-tab-roles',
        renderIcon: (selected: boolean) => {
          return (
            <GSIcon
              iconName="gs_shield_check"
              size={20}
              css={selected ? selectedIconStyles : iconStyles}
            />
          )
        },
      },
    ]

    return items.filter((item) => {
      return availableTabs.includes(item.value)
    })
  }, [availableTabs, isRolesLoading, isUsersLoading, roles.length, users.length])

  return <TableTabs<InternalAccessTab> tabs={tabs} selectedValue={selectedTab} onClick={onChange} />
}

export const InternalAccessTabs = memo(InternalAccessTabsComponent)
